<template>
  <div class="centered-container">
    <v-card class="elevation-3">
      <div class="title">
        <img src="/scrapingant-logo.png" />
        <p class="lg--text mb-0 fontweight400">Authorize application</p>
      </div>

      <div v-if="state === 'loading'" class="loading-overlay">
        <v-progress-circular :size="50" :width="2" color="primary" indeterminate></v-progress-circular>
      </div>

      <div v-else-if="state === 'expired'" class="title">
        <p class="mb-0 xs--text line-height24 fontweight400">
          This authorization request has expired or was already used.
        </p>
        <br />
        <p class="mb-0 xs--text line-height24 fontweight400">
          Please start the login again from your application (for example run <b>/mcp</b> in Claude Code).
        </p>
      </div>

      <div v-else-if="state === 'error'" class="title">
        <p class="mb-0 xs--text line-height24 fontweight400">
          Could not load the authorization request. Please try again.
        </p>
        <div class="actions d-flex justify-end">
          <v-btn color="primary" @click="load">Retry</v-btn>
        </div>
      </div>

      <div v-else-if="state === 'redirecting'" class="title">
        <p class="mb-0 xs--text line-height24 fontweight400">
          Returning to <b>{{ clientLabel }}</b>...
        </p>
      </div>

      <div v-else>
        <div class="title">
          <p class="mb-0 xs--text line-height24 fontweight400">
            <b>{{ clientLabel }}</b> wants to scrape web pages using your ScrapingAnt account.
          </p>
          <br />
          <p class="mb-0 xs--text line-height24 fontweight400">
            Requests made by it will use the credits of the account you are logged in with.
            The application never sees your API key; you can revoke access by logging out of it.
          </p>
          <br />
          <p class="mb-0 xs--text line-height24 fontweight400">
            After approving you will be sent to <b>{{ destinationLabel }}</b>.
          </p>
        </div>
        <span class="form-error">{{ error }}</span>
        <div class="actions">
          <v-row class="justify-space-between ma-0">
            <v-col class="pa-0">
              <v-btn color="font" :disabled="busy" @click="deny">Deny</v-btn>
            </v-col>
            <v-col class="pa-0" align="end">
              <v-btn color="primary" :disabled="busy" @click="approve">Allow</v-btn>
            </v-col>
          </v-row>
        </div>
        <div v-if="!busy" class="actions d-flex justify-end reset">
          <a href="#" @click.prevent="switchAccount">Not you? Log in with another account</a>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import {clearOAuthRequest, pendingOAuthRequest, rememberOAuthRequest} from "@/utils/oauth_consent";

const LOOPBACK_HOSTS = ["localhost", "127.0.0.1", "[::1]", "::1"];

export default {
  name: "OAuthConsent",
  data() {
    return {
      requestId: null,
      consent: null,
      state: "loading",
      busy: false,
      error: "",
    };
  },
  computed: {
    clientLabel() {
      return (this.consent && this.consent.client_name) || "An application";
    },
    destinationLabel() {
      if (!this.consent) {
        return "";
      }
      const host = this.consent.redirect_host || "";
      if (LOOPBACK_HOSTS.includes(host)) {
        return "an application running on this computer";
      }
      if (host === "claude.ai") {
        return "Claude";
      }
      return host;
    },
  },
  created() {
    this.requestId = this.$route.query.request_id || pendingOAuthRequest();
    if (!this.requestId) {
      this.state = "expired";
      return;
    }
    rememberOAuthRequest(this.requestId);
    this.load();
  },
  methods: {
    load() {
      this.state = "loading";
      this.$store.dispatch("oauth/getConsent", this.requestId).then(
        (res) => {
          this.consent = res.data;
          this.state = "ready";
        },
        (err) => {
          const status = err && err.response && err.response.status;
          if (status === 404) {
            // Only the server's not-found answer means the request is gone; a transient failure keeps it.
            clearOAuthRequest();
            this.state = "expired";
          } else {
            this.state = "error";
          }
        }
      );
    },
    approve() {
      this.decide("oauth/approve");
    },
    deny() {
      this.decide("oauth/deny");
    },
    decide(action) {
      this.busy = true;
      this.error = "";
      this.$store.dispatch(action, this.requestId).then(
        (res) => {
          clearOAuthRequest();
          this.state = "redirecting";
          window.location.assign(res.data.redirect_to);
        },
        (err) => {
          this.busy = false;
          const status = err && err.response && err.response.status;
          if (status === 404) {
            clearOAuthRequest();
            this.state = "expired";
          } else if (status === 403) {
            this.error = "Your API key is not active. Please check your subscription in the dashboard.";
          } else {
            this.error = "Something went wrong. Please try again.";
          }
        }
      );
    },
    switchAccount() {
      // Block decisions while the account is being changed; the request id is kept so the
      // consent page comes back after the new login.
      this.busy = true;
      this.$auth.logout({redirect: {name: "Login"}});
    },
  },
};
</script>
